import _ from 'lodash';
import * as postMessage from './privates/postMessage';
import platformUtils from './privates/platformUtils';
import {errors} from './privates/validations/errors';
import monitoring from './privates/monitoring';

const TYPES = {
    LOCAL: 'local',
    SESSION: 'session',
    MEMORY: 'memory'
};

const getParsedStoredAppData = (type, appId, storageObject) => {
    return JSON.parse(_.get(storageObject, [`${type}Storage`, appId], '{}'));
};

const updateBrowserStorage = (type, appId, appDataToStore) => {
    postMessage.sendMessageToHost(postMessage.Intent.BROWSER_API, {
        command: postMessage.MessageTypes.SET_TO_STORAGE,
        appDefinitionId: appId,
        data: {
            type: `${type}Storage`,
            key: appId,
            value: appDataToStore
        }});
};

/**
 * @namespace wix-storage
 * @summary The wix-storage module contains functionality for the persistent
 *  storage of key/value data in the user's browser.
 * @description
 *  There are three types of storage:
 *
 *  + **Local:** Data in local storage never expires, even if the user closes
 *    your page. When the user reopens the page later, the data can still be
 *    retrieved.
 *
 *  + **Session:** Data in session storage is available while the user's web
 *    session is active. The session ends when the user closes the browser tab
 *    or window. When the session ends, all the data in session storage is lost.
 *    Reloading or restoring the page does not affect session storage data.
 * 
 *  + **Memory:** Data in memory storage is available as long the site visitor does
 *    not refresh or close the page. Reloading or restoring the page clears the memory
 *    storage data. You can store up to 1mb of data in memory storage.
 *
 *
 *  To use the Storage API, import the needed storage type(s) from the
 *  `wix-storage` module:
 *
 *    ```javascript
 *    import {local, session, memory} from 'wix-storage';
 *
 *    // Or one of:
 *
 *    import {local} from 'wix-storage';
 *    import {session} from 'wix-storage';
 *    import {memory} from 'wix-storage';
 *    ```
 *
 *  The APIs in `wix-storage` can only be used in front-end code.
 *
 *  Some older browsers may not fully support `wix-storage`. See
 *  <a href="https://developer.mozilla.org/en-US/docs/Web/API/Storage#Browser_compatibility" target=_blank>Browser
 *  compatibility</a> for more information.
 */

/**
 * @member local
 * @summary Used for local storage of data.
 * @description Use the methods in [`Storage`](wix-storage.Storage.html) to manage `local` data.
 * @snippet [Storage-setItem_local.es6=Store an item in local storage]
 * @snippet [Storage-getItem_local.es6=Retrieve an item from local storage]
 * @snippet [Storage-removeItem_local.es6=Remove an item from local storage]
 * @snippet [Storage-clear_local.es6=Remove all items from local storage]
 * @type wix-storage.Storage
 * @memberof wix-storage
 */

/**
 * @member memory
 * @summary Used for memory storage of data.
 * @description Use the methods in [`Storage`](wix-storage.Storage.html) to manage `memory` data.
 * @snippet [Storage-setItem_memory.es6=Store an item in memory storage]
 * @snippet [Storage-getItem_memory.es6=Retrieve an item from memory storage]
 * @snippet [Storage-removeItem_memory.es6=Remove an item from memory storage]
 * @snippet [Storage-clear_memory.es6=Remove all items from memory storage]
 * @type wix-storage.Storage
 * @memberof wix-storage
 */

/**
 * @member session
 * @summary Used for session storage of data.
 * @description Use the methods in [`Storage`](wix-storage.Storage.html) to manage `session` data.
 * @snippet [Storage-setItem_session.es6=Store an item in session storage]
 * @snippet [Storage-getItem_session.es6=Retrieve an item from session storage]
 * @snippet [Storage-removeItem_session.es6=Remove an item from session storage]
 * @snippet [Storage-clear_session.es6=Remove all items from session storage]
 * @type wix-storage.Storage
 * @memberof wix-storage
 */

/**
 * @class Storage
 * @summary Used for storing local or session key/value data in the user's browser.
 * @description
 *  To learn more about local, session, or memory storage, see [`wix-storage`](wix-storage.html).
 *
 *  To use the Storage API, import the needed storage type(s) from the
 *  `wix-storage` module:
 *
 *    ```javascript
 *    import {local, session, memory} from 'wix-storage';
 *
 *    // Or one of:
 *
 *    import {local} from 'wix-storage';
 *    import {session} from 'wix-storage';
 *    import {memory} from 'wix-storage';
 *    ```
 *
 * @memberof wix-storage
 */
const methods = {

    /**
     * @function setItem
     * @summary Stores an item in local, session, or memory storage.
     * @param {external:String} key The key of item to set.
     * @param {external:String} value The value of the item to set.
     * @memberof wix-storage.Storage
     * @snippet [Storage-setItem_local.es6=Store an item in local storage]
     * @snippet [Storage-setItem_session.es6=Store an item in session storage]
     * @snippet [Storage-setItem_memory.es6=Store an item in memory storage]
     * @see [getItem( )](#getItem)
     */
    setItem: (type, appId, storageObject, key, value) => {
        const newData = {[String(key)]: String(value)};
        const appDataToStore = JSON.stringify(_.assign({}, getParsedStoredAppData(type, appId, storageObject), newData));

        const maxDataSize = type === 'memory' ? 1000000 : 50000;
        if (appDataToStore.length > maxDataSize) {
            throw new monitoring.MonitoringErrorToIgnore(errors.ERROR_STORAGE_MAX_SIZE);
        }
        _.set(storageObject, [`${type}Storage`, appId], appDataToStore);
        updateBrowserStorage(type, appId, appDataToStore);
    },

    /**
     * @function getItem
     * @summary Gets an item from local, session, or memory storage.
     * @param {external:String} key The key of the item to get.
     * @returns {external:String} The retrieved item value.
     * @memberof wix-storage.Storage
     * @snippet [Storage-getItem_local.es6=Retrieve an item from local storage]
     * @snippet [Storage-getItem_session.es6=Retrieve an item from session storage]
     * @snippet [Storage-getItem_memory.es6=Retrieve an item from memory storage]
     * @see [setItem( )](#setItem)
     */
    getItem: (type, appId, storageObject, key) => {
        const storedAppData = getParsedStoredAppData(type, appId, storageObject);
        return _.get(storedAppData, String(key), null);
    },

    /**
     * @function removeItem
     * @summary Removes an item from local, session, or memory storage.
     * @param {external:String} key The key of the item to remove.
     * @memberof wix-storage.Storage
     * @snippet [Storage-removeItem_local.es6=Remove an item from local storage]
     * @snippet [Storage-removeItem_session.es6=Remove an item from session storage]
     * @snippet [Storage-removeItem_memory.es6=Remove an item from memory storage]
     * @see [clear( )](#clear)
     */
    removeItem: (type, appId, storageObject, key) => {
        const storedAppData = getParsedStoredAppData(type, appId, storageObject);
        const appDataToStore = JSON.stringify(_.omit(storedAppData, key));

        _.set(storageObject, [`${type}Storage`, appId], appDataToStore);
        updateBrowserStorage(type, appId, appDataToStore);
    },

    /**
     * @function clear
     * @summary Removes **all** items from local, session, or memory storage.
     * @memberof wix-storage.Storage
     * @snippet [Storage-clear_local.es6=Remove all items from local storage]
     * @snippet [Storage-clear_session.es6=Remove all items from session storage]
     * @snippet [Storage-clear_memory.es6=Remove all items from memory storage]
     * @see [remove( )](#remove)
     */
    clear: (type, appId, storageObject) => {
        const appDataToStore = '{}';
        _.set(storageObject, [`${type}Storage`, appId], appDataToStore);
        updateBrowserStorage(type, appId, appDataToStore);
    }
};

const createBoundMethod = (storageType, appId, storageObject, methodName) => {
    return {[methodName]: methods[methodName].bind(null, storageType, appId, storageObject)};
};

const buildAPIObject = (storageType, appId, storageObject) => {
    const methodNames = ['setItem', 'getItem', 'removeItem', 'clear'];
    return methodNames.reduce((acc, methodName) => _.assign(acc, createBoundMethod(storageType, appId, storageObject, methodName)), {});
};

const setStorageObject = (storageObject, storageData) => {
    const browserParsedStoredData = JSON.parse(storageData);
    _.set(storageObject, `${TYPES.LOCAL}Storage`, _.get(browserParsedStoredData, TYPES.LOCAL));
    _.set(storageObject, `${TYPES.SESSION}Storage`, _.get(browserParsedStoredData, TYPES.SESSION));
    _.set(storageObject, `${TYPES.MEMORY}Storage`, _.get(browserParsedStoredData, TYPES.MEMORY));
};

class Storage {
    constructor() {
        this._storageObject = {};
        setStorageObject(this._storageObject, platformUtils.getQueryParameter('storage'));
    }

    createScopedStorage(appId, storageValue) {
        setStorageObject(this._storageObject, storageValue);
        postMessage.registerEventListener(postMessage.MessageTypes.STORAGE_UPDATE, (evtData) => {
            setStorageObject(this._storageObject, evtData.storageValue);
        });
        return {
            local: buildAPIObject(TYPES.LOCAL, appId, this._storageObject),
            session: buildAPIObject(TYPES.SESSION, appId, this._storageObject),
            memory: buildAPIObject(TYPES.MEMORY, appId, this._storageObject)
        };
    }
}

export default Storage;
